/**
 * stagePlayer — Stage-only audio playback for narration.
 *
 * Implements:
 * - unlock(): creates/resumes AudioContext + plays a silent buffer inside the
 *   Start tap gesture (iOS/Safari autoplay requirement).
 * - enqueue(): fetches /api/audio/:id → decodes → plays sequentially (queue,
 *   never overlapping). captionOnly beats (null audioId) skip audio silently.
 * - stop(): halts current source + drains queue.
 *
 * Only the Stage device ever creates this player; phones get captionOnly null.
 * Errors (404, decode failure, network) are swallowed silently — the caption
 * is already shown regardless.
 *
 * Signal graph (all under one master gain so host mute / ctx.suspend catch
 * everything):
 *
 *   looping ambient layers ─▶ bedBus ─┐
 *   one-shot stingers ──────▶ sfxBus ─┼─▶ master gain ─▶ destination
 *   narration clips ───────────────────┘  (per-clip narration gain feeds master)
 *
 * bedBus/sfxBus are the ducking handles: while narration speaks they ramp down
 * to the tuned duck levels and back up when it stops (see setSpeaking). Their
 * resting value is 1 — the per-layer/one-shot gains carry the actual mix levels
 * (from tuning.json). Narration stays direct → master (never ducked).
 */
import { getTuning, subscribeTuning } from './audioTuning.js';
import { netBase } from '../discord/embedded.js';

/**
 * One looping ambient layer (URL + gain). The ambient engine holds several of
 * these under bedBus, separate from the narration queue's gain.
 */
export interface AmbientLayer {
  url: string;
  gain: number;
  /**
   * Which tuning gain slider governs this layer. Live tuning edits re-ramp the
   * layer to `getTuning().gains[role]`; layers with no role are left untouched.
   */
  role?: 'bed' | 'layer' | 'day' | 'lobby';
}

export interface StagePlayer {
  /** Prime/unlock the AudioContext from a user gesture (the Start tap). */
  unlock(): void;
  /**
   * Try to unlock WITHOUT a user gesture — for surfaces that should already be
   * scored when they appear (the game-select screen's menu music). Inside a
   * Discord Activity the launch click usually carries the gesture through, so
   * the context starts `running`; in a cold browser tab it stays `suspended`
   * and this is a silent no-op until `unlock()` (or the global click handler)
   * runs inside a real gesture. Never throws.
   */
  tryUnlock(): void;
  /** Whether audio has been unlocked. */
  readonly isUnlocked: boolean;
  /**
   * Subscribe to the unlock transition (fires once, immediately if already
   * unlocked). Lets a screen ask for music before the browser allows it and get
   * it the moment a gesture lands. Returns an unsubscribe fn.
   */
  onUnlocked(cb: () => void): () => void;
  /**
   * Queue a narration beat for playback. `audioId` fetches bytes from
   * /api/audio/:id; when audioId is null (caption-only / fallback), no audio
   * plays but the queue continues.
   */
  enqueue(audioId: string | null, text: string): void;
  /** Stop playback and clear the queue. */
  stop(): void;
  /** Suspend the AudioContext — pauses whatever is currently speaking. */
  pause(): void;
  /** Resume a paused AudioContext. */
  resume(): void;
  /** Whether playback is currently paused (context suspended by the host). */
  readonly isPaused: boolean;
  /** Mute/unmute all narration output (via a GainNode). */
  setMuted(muted: boolean): void;
  /** Current mute state. */
  readonly isMuted: boolean;
  /**
   * Master volume 0..1 — everything (narration + ambient + stingers) ramps
   * live. Persists across UI mounts (the popover re-reads `masterVolume`).
   * Independent of mute: the value is remembered while muted and re-applied
   * on unmute.
   */
  setVolume(v: number): void;
  /** Current master volume 0..1 (what the volume slider should show). */
  readonly masterVolume: number;

  /** True while a narration clip is actively playing (drives the host status). */
  readonly isSpeaking: boolean;
  /**
   * Subscribe to speaking-state changes (start/stop of narration playback).
   * Returns an unsubscribe fn. Fires only on real transitions.
   */
  onSpeaking(cb: (speaking: boolean) => void): () => void;

  // ── Ambient soundscape (Stage-only, separate from the narration queue) ──────
  /**
   * Start (or replace) the set of LOOPING ambient layers. Each layer fetches its
   * URL, decodes, and loops under a shared ambient master gain. Calling again
   * cross-fades to the new set. No-op if audio isn't unlocked yet.
   */
  setAmbient(layers: AmbientLayer[]): void;
  /** Fade out and stop ALL looping ambient layers (default from tuning). */
  stopAmbient(fadeMs?: number): void;
  /**
   * Play a ONE-SHOT stinger over the ambient bed (wolves/seer/wake) via sfxBus.
   * Does not touch the looping layers. Respects mute/pause. No-op if not
   * unlocked. `gain` defaults to the tuned SFX level; `delayMs` schedules the
   * start relative to now (for choreographed stingers).
   */
  playOneShot(url: string, gain?: number, delayMs?: number): void;
  /**
   * A UI feedback one-shot (button click, card hover) through the SFX bus at
   * the tuned `ui` gain. Same path as playOneShot but always quiet and never
   * choreographed; `gainScale` (default 1) trims an individual sound relative
   * to that level (e.g. the outside-click blip).
   */
  playUi(url: string, gainScale?: number): void;
  /**
   * A tiny synthesized "pop" blip (no asset) through the SFX bus — used when a
   * player posts a new whisper. Respects unlock/mute/pause and master volume.
   */
  playPop(): void;
  /**
   * A synthesized chain-clank (no asset) through the SFX bus — used when a
   * Paladin freezes a player. Respects unlock/mute/pause and master volume.
   */
  playFreeze(): void;

  // ── Narration pacing / warm-up ──────────────────────────────────────────────
  /**
   * Hold the narration queue: any beat WITH audio waits until `epochMs`
   * (Date.now() clock) before playing, capped at 5s so a bad clock can never
   * deadlock the game. Caption-only beats ignore the hold. Used to let a phase
   * stinger land before the narrator speaks. stop() clears the hold.
   */
  holdNarrationUntil(epochMs: number): void;
  /**
   * Fire-and-forget warm of the decode cache for the given URLs so later
   * one-shots/loops start on schedule. No-op if audio isn't unlocked yet.
   */
  preload(urls: string[]): void;
  /**
   * Decoded duration of an asset in SECONDS (fetches + decodes on first use,
   * cached after). null if audio isn't unlocked or the asset can't be decoded.
   * Accepts the same URLs as playOneShot (used by the studio timeline to draw
   * true-to-length bars).
   */
  getDuration(url: string): Promise<number | null>;
}

interface QueueEntry {
  audioId: string | null;
  text: string;
}

/** Stable identity of a requested ambient set (url+gain+role, order-insensitive). */
function ambientKey(layers: AmbientLayer[]): string {
  return layers
    .map((l) => `${l.url}|${l.gain}|${l.role ?? ''}`)
    .sort()
    .join('§');
}

/** A live looping ambient layer (its source + per-layer gain). */
interface LiveAmbientLayer {
  url: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
  /** Tuning slider governing this layer, so live edits can re-ramp it. */
  role?: 'bed' | 'layer' | 'day' | 'lobby';
}

class StagePlayerImpl implements StagePlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private unlocked = false;
  private queue: QueueEntry[] = [];
  private playing = false;
  private currentSource: AudioBufferSourceNode | null = null;
  private paused = false;
  private muted = false;
  /** Master volume 0..1 — survives popover unmounts; applied unless muted. */
  private volume = 1;
  private speaking = false;
  /** Text of the beat currently being spoken ('' when silent) — the dedupe key. */
  private speakingText = '';
  private readonly speakingListeners = new Set<(s: boolean) => void>();

  // Ambient soundscape — a separate signal path from the narration queue.
  // bedBus carries the looping layers, sfxBus the one-shots; both are the
  // ducking handles (ramped down while narration speaks). They sit under the
  // shared master gain.
  private bedBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private ambientLayers: LiveAmbientLayer[] = [];
  /** Decode cache so re-triggering the same asset doesn't re-fetch. */
  private bufferCache = new Map<string, AudioBuffer>();
  /** Monotonic token so a stale setAmbient's async decode can't revive a stopped bed. */
  private ambientToken = 0;
  /**
   * Identity of the CURRENTLY REQUESTED looping set (not the decoded one) so a
   * repeat request for the same layers is a no-op even before its decode lands.
   * Cleared by stopAmbient.
   */
  private ambientKey = '';
  /** Beats with audio wait until this Date.now() epoch before playing (5s cap). */
  private holdUntil = 0;
  /** Unsubscribe from live tuning changes (set up on first unlock). */
  private tuningUnsub: (() => void) | null = null;
  /** Auto-resume listeners wired once (tab-switch suspend recovery). */
  private autoResumeWired = false;
  /** One-shot listeners waiting for the unlock transition. */
  private readonly unlockListeners = new Set<() => void>();

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  onUnlocked(cb: () => void): () => void {
    if (this.unlocked) {
      cb();
      return () => {/* already fired */};
    }
    this.unlockListeners.add(cb);
    return () => {
      this.unlockListeners.delete(cb);
    };
  }

  /** Flip to unlocked and drain the waiting listeners (each fires at most once). */
  private markUnlocked(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    const waiting = [...this.unlockListeners];
    this.unlockListeners.clear();
    for (const cb of waiting) {
      try {
        cb();
      } catch {
        /* a listener throwing must not break audio */
      }
    }
  }

  get isPaused(): boolean {
    return this.paused;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get isSpeaking(): boolean {
    return this.speaking;
  }

  onSpeaking(cb: (speaking: boolean) => void): () => void {
    this.speakingListeners.add(cb);
    return () => {
      this.speakingListeners.delete(cb);
    };
  }

  /** Flip the speaking flag and notify listeners — only on a real transition. */
  private setSpeaking(next: boolean): void {
    if (this.speaking === next) return;
    this.speaking = next;
    // Duck the ambient buses so narration reads clearly, then release.
    this.applyDucking(next);
    for (const cb of this.speakingListeners) {
      try {
        cb(next);
      } catch {
        /* a listener throwing must not break playback */
      }
    }
  }

  /**
   * Ramp bedBus/sfxBus to the tuned duck levels while speaking, back to 1 when
   * not. Uses cancelScheduledValues + setValueAtTime(current) so an in-flight
   * ramp is picked up smoothly rather than snapping. No-op without a ctx/buses.
   */
  private applyDucking(speaking: boolean): void {
    if (!this.ctx) return;
    const { ducking } = getTuning();
    const now = this.ctx.currentTime;
    const rampSec = (speaking ? ducking.attackMs : ducking.releaseMs) / 1000;
    const bedTarget = speaking ? ducking.bedLevel : 1;
    const sfxTarget = speaking ? ducking.sfxLevel : 1;
    for (const [bus, target] of [
      [this.bedBus, bedTarget],
      [this.sfxBus, sfxTarget],
    ] as const) {
      if (!bus) continue;
      try {
        bus.gain.cancelScheduledValues(now);
        bus.gain.setValueAtTime(bus.gain.value, now);
        bus.gain.linearRampToValueAtTime(target, now + rampSec);
      } catch {
        /* node detached — ignore */
      }
    }
  }

  /** Lazily create (once) the master GainNode that all sources route through. */
  private ensureGain(): GainNode | null {
    if (!this.ctx) return null;
    if (!this.gain) {
      this.gain = this.ctx.createGain();
      this.gain.gain.value = this.muted ? 0 : this.volume;
      this.gain.connect(this.ctx.destination);
    }
    return this.gain;
  }

  /**
   * Lazily create the bed sub-bus (all looping layers route through it). It
   * feeds the SAME master gain as narration, so a host mute silences ambient
   * too and ctx.suspend() (pause) freezes the loops. Resting gain is 1 (ducking
   * lowers it while speaking; per-layer gains carry the mix levels).
   */
  private ensureBedBus(): GainNode | null {
    const master = this.ensureGain();
    if (!this.ctx || !master) return null;
    if (!this.bedBus) {
      this.bedBus = this.ctx.createGain();
      this.bedBus.gain.value = this.speaking ? getTuning().ducking.bedLevel : 1;
      this.bedBus.connect(master);
    }
    return this.bedBus;
  }

  /**
   * Lazily create the SFX sub-bus (all one-shot stingers route through it).
   * Same master routing as bedBus; ducked to `ducking.sfxLevel` while speaking.
   */
  private ensureSfxBus(): GainNode | null {
    const master = this.ensureGain();
    if (!this.ctx || !master) return null;
    if (!this.sfxBus) {
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.gain.value = this.speaking ? getTuning().ducking.sfxLevel : 1;
      this.sfxBus.connect(master);
    }
    return this.sfxBus;
  }

  /**
   * Wire up live tuning changes (once, on first unlock): re-ramp each live
   * ambient layer to its role's gain, and re-apply ducking so slider moves are
   * audible immediately without a reload.
   */
  private ensureTuningSub(): void {
    if (this.tuningUnsub) return;
    this.tuningUnsub = subscribeTuning((t) => {
      if (!this.ctx) return;
      const now = this.ctx.currentTime;
      for (const layer of this.ambientLayers) {
        if (!layer.role) continue; // no slider governs this layer
        const target = t.gains[layer.role];
        try {
          layer.gain.gain.cancelScheduledValues(now);
          layer.gain.gain.setValueAtTime(layer.gain.gain.value, now);
          layer.gain.gain.linearRampToValueAtTime(target, now + 0.1);
        } catch {
          /* node detached — ignore */
        }
      }
      // Re-assert bus levels: duck targets if speaking, otherwise resting 1.
      this.applyDucking(this.speaking);
    });
  }

  /** Fetch + decode an asset, caching the decoded buffer. */
  private async loadBuffer(url: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    const cached = this.bufferCache.get(url);
    if (cached) return cached;
    try {
      // Inside a Discord Activity, same-origin asset fetches route through the
      // `/.proxy` prefix (netBase()); outside Discord netBase() is '' (no change).
      const resp = await fetch(`${netBase()}${url}`);
      if (!resp.ok) return null;
      const bytes = await resp.arrayBuffer();
      const buf = await this.ctx.decodeAudioData(bytes);
      this.bufferCache.set(url, buf);
      return buf;
    } catch {
      return null;
    }
  }

  setAmbient(layers: AmbientLayer[]): void {
    if (!this.ctx || !this.unlocked) return;
    const bus = this.ensureBedBus();
    if (!bus) return;
    // Already playing exactly this set → leave it alone. Without this, the
    // game-select screen's menu music would restart from zero the moment the
    // LOBBY soundscape asked for the same track.
    const key = ambientKey(layers);
    if (key === this.ambientKey) return;
    this.ambientKey = key;
    const token = ++this.ambientToken;
    const { fades } = getTuning();

    // Cross-fade: fade out the old layers (short) and stop them, then bring the
    // new ones up. Kept simple — a hard-ish swap under a low bus is fine.
    this.fadeOutLayers(this.ambientLayers, fades.ambientCrossOutMs);
    this.ambientLayers = [];

    const crossInSec = fades.ambientCrossInMs / 1000;
    for (const layer of layers) {
      void this.loadBuffer(layer.url).then((buf) => {
        // A newer setAmbient/stopAmbient superseded this decode → discard.
        if (!buf || !this.ctx || token !== this.ambientToken) return;
        const g = this.ctx.createGain();
        g.gain.value = 0;
        g.connect(bus);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        src.connect(g);
        src.start(0);
        // Fade the layer in.
        const now = this.ctx.currentTime;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(layer.gain, now + crossInSec);
        this.ambientLayers.push({ url: layer.url, source: src, gain: g, role: layer.role });
      });
    }
  }

  stopAmbient(fadeMs?: number): void {
    const ms = fadeMs ?? getTuning().fades.ambientStopMs;
    this.ambientKey = '';
    // Invalidate any in-flight decodes so they don't start after we stop.
    this.ambientToken++;
    this.fadeOutLayers(this.ambientLayers, ms);
    this.ambientLayers = [];
  }

  /** Ramp the given layers to silence over `fadeMs`, then stop their sources. */
  private fadeOutLayers(layers: LiveAmbientLayer[], fadeMs: number): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const layer of layers) {
      try {
        layer.gain.gain.cancelScheduledValues(now);
        layer.gain.gain.setValueAtTime(layer.gain.gain.value, now);
        layer.gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
        layer.source.stop(now + fadeMs / 1000 + 0.05);
      } catch {
        /* already stopped */
      }
    }
  }

  playOneShot(url: string, gain?: number, delayMs?: number): void {
    if (!this.ctx || !this.unlocked) return;
    const bus = this.ensureSfxBus();
    if (!bus) return;
    const level = gain ?? getTuning().gains.sfx;
    void this.loadBuffer(url).then((buf) => {
      if (!buf || !this.ctx) return;
      const g = this.ctx.createGain();
      g.gain.value = level;
      g.connect(bus);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(g);
      // Schedule (choreographed) start relative to now.
      src.start(this.ctx.currentTime + (delayMs ?? 0) / 1000);
    });
  }

  playUi(url: string, gainScale = 1): void {
    // Muted/paused suppress UI feedback too — a click blip during a host pause
    // would be the only thing audible.
    if (!this.ctx || !this.unlocked || this.muted || this.paused) return;
    this.playOneShot(url, getTuning().gains.ui * gainScale);
  }

  playPop(): void {
    if (!this.ctx || !this.unlocked || this.muted || this.paused) return;
    const bus = this.ensureSfxBus();
    if (!bus) return;
    const t = this.ctx.currentTime;
    // Soft two-partial "bubble" pop: short pitch drop + fast decay, well under
    // the narration level so it reads as texture, not an alert.
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
    g.connect(bus);
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(720, t);
    osc.frequency.exponentialRampToValueAtTime(340, t + 0.12);
    osc.connect(g);
    osc.start(t);
    osc.stop(t + 0.16);
    osc.onended = () => {
      osc.disconnect();
      g.disconnect();
    };
  }

  playFreeze(): void {
    if (!this.ctx || !this.unlocked || this.muted || this.paused) return;
    const bus = this.ensureSfxBus();
    if (!bus) return;
    const t = this.ctx.currentTime;
    // Two quick detuned metallic strikes + a falling body — reads as a shackle
    // snapping shut. Quiet enough to sit under narration.
    const strike = (at: number, freq: number, peak: number) => {
      if (!this.ctx) return;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(peak, at + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);
      g.connect(bus);
      const osc = this.ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, at);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.55, at + 0.2);
      osc.connect(g);
      osc.start(at);
      osc.stop(at + 0.24);
      osc.onended = () => {
        osc.disconnect();
        g.disconnect();
      };
    };
    strike(t, 1180, 0.07);
    strike(t + 0.07, 880, 0.09);
    strike(t + 0.15, 240, 0.08);
  }

  holdNarrationUntil(epochMs: number): void {
    this.holdUntil = epochMs;
  }

  preload(urls: string[]): void {
    if (!this.ctx || !this.unlocked) return;
    for (const url of urls) {
      void this.loadBuffer(url);
    }
  }

  async getDuration(url: string): Promise<number | null> {
    if (!this.ctx || !this.unlocked) return null;
    const buf = await this.loadBuffer(url);
    return buf ? buf.duration : null;
  }

  pause(): void {
    this.paused = true;
    // suspend() halts the AudioContext clock — the current beat freezes and
    // resumes exactly where it left off.
    this.ctx?.suspend().catch(() => {/* silent */});
  }

  resume(): void {
    this.paused = false;
    this.ctx?.resume().catch(() => {/* silent */});
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    const g = this.ensureGain();
    if (g) g.gain.value = muted ? 0 : this.volume;
  }

  get masterVolume(): number {
    return this.volume;
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.muted) return; // remembered; re-applied on unmute
    const g = this.ensureGain();
    if (!g || !this.ctx) return;
    const now = this.ctx.currentTime;
    try {
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(this.volume, now + 0.08);
    } catch {
      /* node detached — ignore */
    }
  }

  /** Called inside a user gesture — primes the AudioContext for iOS. */
  unlock(): void {
    if (this.unlocked) return;
    try {
      const AudioContextCtor =
        typeof window !== 'undefined'
          ? (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)
          : null;
      if (!AudioContextCtor) return;
      if (!this.ctx) {
        this.ctx = new AudioContextCtor();
      }
      this.ensureGain();
      // Play a 1-frame silent buffer — the gesture-unlock technique for iOS.
      const buf = this.ctx.createBuffer(1, 1, 22050);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.ctx.destination);
      src.start(0);
      this.ctx.resume().catch(() => {/* silent */});
      // Now that we have a ctx, subscribe to live tuning so slider edits re-ramp
      // the buses and live layers in real time.
      this.ensureTuningSub();
      this.ensureAutoResume();
      this.markUnlocked();
    } catch {
      // Ignore — silent degradation; captions still work.
    }
  }

  tryUnlock(): void {
    if (this.unlocked) return;
    try {
      const AudioContextCtor =
        typeof window !== 'undefined'
          ? (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)
          : null;
      if (!AudioContextCtor) return;
      if (!this.ctx) this.ctx = new AudioContextCtor();
      this.ensureGain();
      this.ensureTuningSub();
      this.ensureAutoResume();
      // Unlike unlock(), this runs OUTSIDE a gesture: only claim the unlock once
      // the context is actually running, or a "playing" ambient bed would be
      // silently swallowed by the autoplay policy with no retry.
      const settle = () => {
        if (this.ctx?.state === 'running') this.markUnlocked();
      };
      this.ctx.resume().then(settle, settle);
      settle();
    } catch {
      /* silent — a gesture will unlock later */
    }
  }

  /**
   * Recover from browser-initiated context suspension. Chrome (especially inside
   * the Discord Activity iframe) can suspend/interrupt the AudioContext when the
   * tab is backgrounded — without this, audio stays dead after switching back
   * until the user reconnects the Activity. Resume on return-to-visible, window
   * focus, and (autoplay-policy-proof) the next pointer gesture. NEVER fights an
   * intentional host pause (`this.paused`).
   */
  private ensureAutoResume(): void {
    if (this.autoResumeWired || typeof document === 'undefined') return;
    this.autoResumeWired = true;
    const tryResume = (): void => {
      if (!this.ctx || this.paused) return;
      const state = this.ctx.state as string;
      if (state === 'suspended' || state === 'interrupted') {
        this.ctx.resume().catch(() => {/* silent */});
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tryResume();
    });
    window.addEventListener('focus', tryResume);
    // Gesture fallback: if the policy blocked the visibility resume, the next
    // click/tap anywhere revives it.
    document.addEventListener('pointerdown', tryResume, { capture: true, passive: true });
  }

  enqueue(audioId: string | null, text: string): void {
    // The narrator never builds a backlog. Spamming a button that answers with a
    // beat (the lobby's below-the-floor "Raise the curtain") used to queue one
    // full line per press, so the Ringmaster kept talking long after the table
    // stopped pressing. Rule: at most ONE pending beat, and the NEWEST wins —
    // a beat that hasn't started yet is already stale by the time a newer one
    // arrives, and stale narration is worth less than none. Narration never
    // gates the game (the engine has already moved on), so dropping is safe.
    const entry: QueueEntry = { audioId, text };
    // A repeat of what is being said RIGHT NOW adds nothing — drop it outright
    // rather than promising it again the moment the line ends.
    if (text && this.speakingText === text) return;
    if (this.queue.length > 0) this.queue = [entry];
    else this.queue.push(entry);
    if (!this.playing) {
      void this.drain();
    }
  }

  stop(): void {
    this.queue = [];
    this.speakingText = '';
    this.holdUntil = 0; // a stop cancels any pending narration hold
    try {
      this.currentSource?.stop();
    } catch {/* already stopped */}
    this.currentSource = null;
    this.playing = false;
    this.setSpeaking(false);
  }

  private async drain(): Promise<void> {
    if (this.playing || this.queue.length === 0) return;
    this.playing = true;

    while (this.queue.length > 0) {
      const entry = this.queue.shift()!;
      if (entry.audioId && this.ctx && this.unlocked) {
        // Honor a narration hold so a phase stinger lands before the narrator
        // speaks. Cap the wait at 5s so a bad clock can never deadlock the game.
        const wait = this.holdUntil - Date.now();
        if (wait > 0) {
          await new Promise<void>((r) => setTimeout(r, Math.min(wait, 5000)));
        }
        this.speakingText = entry.text;
        this.setSpeaking(true);
        await this.playAudioId(entry.audioId);
        this.speakingText = '';
      }
      // caption-only or audio unavailable → just move on immediately (no wait)
    }

    this.playing = false;
    // Queue drained — narration has fallen silent. Captions may linger; audio
    // has not.
    this.setSpeaking(false);
  }

  private async playAudioId(audioId: string): Promise<void> {
    if (!this.ctx) return;
    try {
      // An id starting with "/" is already a full path (e.g. a studio preview
      // URL) — fetch it as-is; otherwise it's a server audio id. Inside a Discord
      // Activity the request routes through the `/.proxy` prefix (netBase());
      // outside Discord netBase() is '' so the URL is unchanged.
      const path = audioId.startsWith('/') ? audioId : `/api/audio/${audioId}`;
      const resp = await fetch(`${netBase()}${path}`);
      if (!resp.ok) return; // 404 or server error — skip silently
      const arrayBuffer = await resp.arrayBuffer();
      const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);

      await new Promise<void>((resolve) => {
        const src = this.ctx!.createBufferSource();
        src.buffer = audioBuffer;
        // Route narration through a per-source gain (so the mixer's narration
        // slider works) into the master gain (so mute/volume applies). Narration
        // is NOT ducked — it feeds master directly, bypassing bedBus/sfxBus.
        const master = this.ensureGain() ?? this.ctx!.destination;
        const g = this.ctx!.createGain();
        g.gain.value = getTuning().gains.narration;
        g.connect(master);
        src.connect(g);
        src.onended = () => {
          this.currentSource = null;
          resolve();
        };
        this.currentSource = src;
        src.start(0);
      });
    } catch {
      // Network error, decode error, or stop() called mid-play — skip silently.
      this.currentSource = null;
    }
  }
}

/** Singleton Stage player (only meaningful on the Stage device). */
export const stagePlayer: StagePlayer = new StagePlayerImpl();
